/**
 * Status of a Purchase Order Revision.
 */
export enum PurchaseOrderRevisionStatus {
  /**
   * The revision is waiting for PD to review.
   */
  REVISION_PENDING_PD = 'REVISION_PENDING_PD',
  /**
   * The revision is in negotiation by PD.
   */
  REVISION_PD_IN_NEGOTIATION = 'REVISION_PD_IN_NEGOTIATION',
  /**
   * The revision is in draft state.
   * Vendor can still edit the revision.
   * Internal has to Negotiate, Approve, Reject or Cancel the revision.
   */
  REVISION_REQUESTED = 'REVISION_REQUESTED',
  /**
   * The revision is in negotiation state.
   * Vendor can still edit the revision.
   * Internal has to Request NS Update, Approve, Reject or Cancel the revision.
   */
  REVISION_NEGOTIATION = 'REVISION_NEGOTIATION',
  /**
   * The revision was approved by internal and sent to NetSuite.
   * Vendor and Internal cannot edit the revision.
   * NetSuite has to acknowledge the revision update.
   */
  REVISION_APPROVED = 'REVISION_APPROVED',
  /**
   * The revision was approved by internal, sent to NetSuite or skipped, and require PO Management update.
   * Vendor cannot edit the revision.
   * Internal has to Mark as NS Updated.
   */
  REVISION_READY_FOR_NS_UPDATE = 'REVISION_READY_FOR_NS_UPDATE',
  /**
   * The revision was processed by NetSuite and/or marked as updated by internal.
   * Vendor and Internal cannot edit the revision.
   */
  REVISION_COMPLETED = 'REVISION_COMPLETED',
  /**
   * The purchase order was cancelled by internal.
   * Vendor and Internal cannot edit the revision.
   * NetSuite has to acknowledge the purchase order cancellation.
   */
  REVISION_CANCELLED = 'REVISION_CANCELLED',
  /**
   * The purchase order was cancelled by NetSuite.
   * Vendor and Internal cannot edit the revision as the PO is cancelled.
   */
  REVISION_CANCELLED_BY_NS = 'REVISION_CANCELLED_BY_NS',
  /**
   * The revision was rejected by internal.
   * Vendor can submit a new revision which will move to REVISION_REQUESTED.
   * Internal cannot do anything.
   */
  REVISION_REJECTED = 'REVISION_REJECTED',
}

/**
 * Information of a user that make an action on a Purchase Order Revision.
 */
export type PurchaseOrderRevisionUser = {
  /**
   * The user id.
   */
  id: string;
  /**
   * The first name and last name of the user.
   */
  name: string;
};

export type PurchaseOrderRevisionUpdatesDueDate = {
  /**
   * The new fob ship date (UTC string).
   */
  new_fob_ship_date_value?: string;
  /**
   * The new due date (UTC string).
   * Should be new_fob_ship_date_value + 14 days if new_fob_ship_date_value is provided.
   */
  new_value: string;
  /**
   * The reason why the due date was updated.
   */
  late_reason: string;
  /**
   * The note added if the late reason is `OTR` (Other).
   */
  note?: string;
};

export type PurchaseOrderRevisionUpdates = {
  due_date?: PurchaseOrderRevisionUpdatesDueDate;
  items?: PurchaseOrderRevisionUpdatesItem[];
};

export type PurchaseOrderRevision = {
  /**
   * The status of the revision.
   */
  status: PurchaseOrderRevisionStatus;
  /**
   * The updates made to the purchase order.
   */
  updates: PurchaseOrderRevisionUpdates;
  /**
   * The vendor user that requested the revision.
   */
  requested_by: PurchaseOrderRevisionUser;
  /**
   * The internal user that started the negotiation.
   */
  negotiated_by?: PurchaseOrderRevisionUser;
  /**
   * The internal user that accepted, rejected or cancelled the revision.
   */
  resolved_by?: PurchaseOrderRevisionUser;
  /**
   * The time the revision was created (UTC string).
   */
  created_at: string;
  /**
   * The last time the revision was updated (UTC string).
   */
  updated_at: string;
  // TODO: add chargeback information
};

export type PurchaseOrderRevisionUpdatesItemSize = {
  /**
   * The line item id of the size that is being updated.
   */
  line_item_id: number;
  /**
   * The new quantity of the size.
   */
  new_quantity: number;
};

export type PurchaseOrderRevisionUpdatesItem = {
  /**
   * The style color of the item.
   */
  style_color: string;
  /**
   * The sizes that are being updated.
   */
  sizes?: PurchaseOrderRevisionUpdatesItemSize[];
  /**
   * The prices that are being updated either Junior or Plus.
   */
  prices?: {
    junior?: number;
    plus?: number;
  };
  /**
   * The comment of the item.
   */
  comment?: string;
};

export type PurchaseOrderRevisionRequestedActivityDetail = {
  reason_for_request?: string;
  chargeback?: {
    type: 'flat' | 'percent';
    value: number;
  };
  due_date?: {
    original_fob_ship_date?: string;
    old_fob_ship_date?: string;
    new_fob_ship_date?: string;
    original_due_date: string;
    old_due_date: string;
    new_due_date: string;
    late_reason?: string;
    note?: string;
  };
  items?: Array<{
    style_color: string;
    name: string;
    image?: string;
    sizes?: Array<{
      size: string;
      old_quantity: number;
      new_quantity?: number;
    }>;
    prices?: {
      original_jr_cost?: number;
      old_jr_cost?: number;
      new_jr_cost?: number;
      original_plus_cost?: number;
      old_plus_cost?: number;
      new_plus_cost?: number;
    };
    comment?: string;
  }>;
};
